/* eslint-disable react/prop-types */
/**
 * Sprint 2 · shared dialogs
 *
 * Discount (Part 5), Split (Part 6), Transfer (Part 7), Reprint (Part 8).
 * All use shadcn Dialog primitives (React Portals) to avoid stacking
 * context bugs and keep the modal-discipline audit clean.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Percent, Split as SplitIcon, ArrowLeftRight, Lock, Printer, X as XIcon } from "lucide-react";
import { TABLES } from "./mockData";
import { ACT, ORDER_STATE, ORDER_TYPE, activeOrderForTable, computeTotals, tableState } from "./posReducer";
import { Dialog, DialogContent } from "../components/ui/dialog";

const fmt2 = (n) => Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// ─── Discount · Part 5 + Membership + Gift voucher (Sprint 3) ──────
// Four modes now: percent, absolute, membership, gift voucher.
//   percent    → order-level percentage discount (Part 5)
//   absolute   → order-level flat rupee discount (Part 5)
//   membership → mock member lookup by phone, member code, or card #.
//                Any of the three resolves to the same member ledger
//                (points × ₹0.10). Cashier redeems N points; the
//                equivalent absolute discount lands on the order.
//   voucher    → gift voucher redemption. Code + authenticate step;
//                on success, voucher value is applied as an absolute
//                discount with reason_code "GIFT_VOUCHER". Applied
//                BEFORE tax so GST base drops correctly.
// One mock member reachable by phone 9876543210, code MEM-1042, or
// card 4111-2222-3333-4444.  One valid voucher: GIFT-500 · ₹500 ·
// valid until 31 Dec 2026. Anything else resolves to not-found.
const MOCK_MEMBERS = {
  by_phone: {
    "9876543210": { name: "Priya Iyer",  points: 2450, tier: "GOLD",     phone: "9876543210", code: "MEM-1042", card: "4111-2222-3333-4444" },
    "9988112233": { name: "Ram Menon",   points:  340, tier: "SILVER",   phone: "9988112233", code: "MEM-1122", card: "4111-2222-3333-2211" },
    "9000000000": { name: "Demo Member", points: 5000, tier: "PLATINUM", phone: "9000000000", code: "MEM-9999", card: "4111-2222-3333-9999" },
  },
};
// Index by member_code and card_number so a single lookup helper hits
// all three surfaces.
const MEMBER_BY_CODE = Object.values(MOCK_MEMBERS.by_phone).reduce((m, x) => (m[x.code] = x, m), {});
const MEMBER_BY_CARD = Object.values(MOCK_MEMBERS.by_phone).reduce((m, x) => (m[x.card.replace(/[^0-9]/g, "")] = x, m), {});
const MEMBER_POINT_VALUE = 0.10; // one point = ten paise

// Mock gift voucher ledger. One valid code, everything else not-found.
const MOCK_VOUCHERS = {
  "GIFT-500": { code: "GIFT-500", value: 500, valid_until: "2026-12-31" },
};

export function DiscountDialog({ open, order, totals, onCancel, onApply }) {
  const [mode, setMode]   = useState("percent");
  const [value, setValue] = useState("");
  // Part 8 · membership sub-state
  // Part 3 (Sprint 3) · Member lookup mode: phone | code | card
  const [memberLookupMode, setMemberLookupMode] = useState("phone");
  const [memberQuery, setMemberQuery] = useState("");
  const [memberStatus, setMemberStatus] = useState("idle"); // idle | searching | found | not_found
  const [member,      setMember]      = useState(null);
  const [redeemPts,   setRedeemPts]   = useState("");
  // Sprint 3 · Gift voucher sub-state
  const [voucherCode, setVoucherCode] = useState("");
  const [voucherStatus, setVoucherStatus] = useState("idle"); // idle | searching | found | not_found
  const [voucher, setVoucher] = useState(null);
  useEffect(() => {
    if (!open) return;
    if (order?.discount) {
      setMode(order.discount.type);
      setValue(String(order.discount.value));
    } else {
      setMode("percent"); setValue("");
    }
    setMemberLookupMode("phone"); setMemberQuery(""); setMemberStatus("idle");
    setMember(null); setRedeemPts("");
    setVoucherCode(""); setVoucher(null); setVoucherStatus("idle");
  }, [open, order?.discount]);
  useEffect(() => {
    if (!open) return;
    const h = (e) => {
      if (e.key === "Escape") { e.preventDefault(); onCancel(); }
      if (e.key === "Enter" && mode !== "membership" && Number(value) > 0) {
        e.preventDefault();
        onApply({ type: mode, value: Number(value) });
      }
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [open, mode, value, onCancel, onApply]);
  // Part 4 (Sprint 4) · auto-fire lookup the moment the member phone
  // hits ten digits. Must be declared BEFORE any early return so hook
  // order stays deterministic across renders.
  useEffect(() => {
    if (!open) return;
    if (memberLookupMode !== "phone") return;
    const digits = String(memberQuery || "").replace(/[^0-9]/g, "");
    if (digits.length !== 10) return;
    setMemberStatus("searching");
    const t = setTimeout(() => {
      const found = MOCK_MEMBERS.by_phone[digits] || null;
      if (found) { setMember(found); setMemberStatus("found"); setRedeemPts(""); }
      else       { setMember(null); setMemberStatus("not_found"); }
    }, 150);
    return () => clearTimeout(t);
  }, [open, memberQuery, memberLookupMode]);
  if (!open) return null;
  // Part 7 · order-level discount applies only to lines WITHOUT a
  // line-level discount. The base surfaced in the preview reflects
  // that exclusion so the cashier sees the honest number.
  const eligibleBase = totals.order_discount_eligible_base != null
    ? totals.order_discount_eligible_base
    : totals.net_subtotal;
  const excludedCount = totals.order_discount_excluded_count || 0;
  const excludedBase  = totals.order_discount_excluded_base  || 0;
  const previewDisc = mode === "percent"
    ? eligibleBase * (Math.min(100, Number(value || 0)) / 100)
    : mode === "membership"
      ? Math.min(Number(redeemPts || 0) * MEMBER_POINT_VALUE, eligibleBase)
      : mode === "voucher"
        ? Math.min(Number(voucher?.value || 0), eligibleBase)
        : Math.min(Number(value || 0), eligibleBase);

  const lookupMember = () => {
    setMemberStatus("searching");
    const q = memberQuery.trim();
    let found = null;
    if (memberLookupMode === "phone")       found = MOCK_MEMBERS.by_phone[q];
    else if (memberLookupMode === "code")   found = MEMBER_BY_CODE[q.toUpperCase()];
    else if (memberLookupMode === "card")   found = MEMBER_BY_CARD[q.replace(/[^0-9]/g, "")];
    // Small delay to make the searching state visible even in mock.
    setTimeout(() => {
      if (!found) { setMember(null); setMemberStatus("not_found"); return; }
      setMember(found); setMemberStatus("found"); setRedeemPts("");
    }, 150);
  };
  const applyMembership = () => {
    if (!member) return;
    const pts = Math.min(Number(redeemPts || 0), member.points);
    const rupees = pts * MEMBER_POINT_VALUE;
    if (!(rupees > 0)) return;
    onApply({
      type: "absolute",
      value: Number(rupees.toFixed(2)),
      reason: "MEMBERSHIP_REDEMPTION",
      authorised_by: `member:${member.phone}`,
      membership_meta: { phone: member.phone, tier: member.tier, points: pts },
    });
  };

  const lookupVoucher = () => {
    setVoucherStatus("searching");
    const code = voucherCode.trim().toUpperCase();
    setTimeout(() => {
      const v = MOCK_VOUCHERS[code];
      if (!v) { setVoucher(null); setVoucherStatus("not_found"); return; }
      setVoucher(v); setVoucherStatus("found");
    }, 150);
  };
  const applyVoucher = () => {
    if (!voucher) return;
    const rupees = Math.min(voucher.value, eligibleBase);
    if (!(rupees > 0)) return;
    onApply({
      type: "absolute",
      value: Number(rupees.toFixed(2)),
      reason: "GIFT_VOUCHER",
      authorised_by: `voucher:${voucher.code}`,
      voucher_meta: { code: voucher.code, value: voucher.value, valid_until: voucher.valid_until },
    });
  };
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onCancel(); }}>
      <DialogContent className="max-w-[600px] max-h-[92vh] p-0 gap-0 border border-[color:var(--pos-color-border,#E2E8DF)] overflow-hidden flex flex-col"
        data-testid="pos-discount-dialog">
        <div className="px-4 py-2 border-b border-[color:var(--pos-color-border,#E2E8DF)] shrink-0">
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-[color:var(--pos-color-secondary-text,#5C6B64)]">
            <Percent className="w-3 h-3" /> Order discount
          </div>
          <div className="text-[13px] font-semibold text-[color:var(--pos-color-primary-text,#1A2421)]">
            Apportioned across food + liquor so per-tax bases stay correct
          </div>
        </div>
        <div className="p-4 space-y-3 flex-1 overflow-y-auto">
          <div className="flex items-center flex-wrap gap-2" data-testid="pos-discount-mode">
            {[["percent", "%"], ["absolute", "₹"], ["membership", "Member"], ["voucher", "Gift voucher"]].map(([m, l]) => (
              <button key={m} type="button" onClick={() => setMode(m)}
                data-testid={`pos-discount-mode-${m}`}
                className={`h-10 px-4 rounded border text-[13px] font-semibold transition
                  ${mode === m
                    ? "border-[color:var(--color-primary)] bg-[color:var(--color-primary)] text-white"
                    : "border-[color:var(--pos-color-border,#E2E8DF)] bg-white text-[color:var(--pos-color-primary-text,#1A2421)] hover:bg-[color:var(--pos-color-raised-surface,#F9F9F8)]"}`}>
                {l}
              </button>
            ))}
            {(mode === "percent" || mode === "absolute") && (
              <input type="number" value={value} min={0}
                onChange={(e) => setValue(e.target.value)} autoFocus
                placeholder={mode === "percent" ? "e.g. 10 (percent)" : "e.g. 200 (rupees)"}
                className="flex-1 min-w-[180px] h-10 rounded border border-[color:var(--pos-color-border,#E2E8DF)] px-3 text-[14px] tabular-nums focus:border-[color:var(--pos-color-accent,var(--color-primary))] focus:outline-none"
                data-testid="pos-discount-value" />
            )}
          </div>

          {mode === "membership" && (
            <div className="space-y-2" data-testid="pos-discount-membership">
              {/* Part 3 (Sprint 3) · lookup mode selector */}
              <div className="flex items-center gap-1" data-testid="pos-membership-lookup-mode">
                {[["phone", "Phone"], ["code", "Member code"], ["card", "Card #"]].map(([m, l]) => (
                  <button key={m} type="button"
                    onClick={() => { setMemberLookupMode(m); setMemberQuery(""); setMember(null); setMemberStatus("idle"); }}
                    data-testid={`pos-membership-lookup-mode-${m}`}
                    className={`h-8 px-3 rounded-full text-[11px] font-medium border transition
                      ${memberLookupMode === m ? "border-[color:var(--color-primary)] bg-[color:var(--color-primary)] text-white" : "border-[color:var(--pos-color-border,#E2E8DF)] bg-white text-[color:var(--pos-color-primary-text,#1A2421)] hover:bg-[color:var(--pos-color-raised-surface,#F9F9F8)]"}`}>
                    {l}
                  </button>
                ))}
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-[color:var(--pos-color-secondary-text,#5C6B64)] mb-1">
                  {memberLookupMode === "phone" ? "Member phone" :
                   memberLookupMode === "code"  ? "Member code"  :
                                                  "Membership card number"}
                </div>
                <div className="flex items-center gap-2">
                  <input type="text" value={memberQuery}
                    onChange={(e) => { setMemberQuery(e.target.value); setMember(null); setMemberStatus("idle"); }}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); lookupMember(); }}}
                    autoFocus
                    placeholder={
                      memberLookupMode === "phone" ? "10-digit phone · try 9876543210" :
                      memberLookupMode === "code"  ? "e.g. MEM-1042" :
                                                     "16-digit card · try 4111 2222 3333 4444"
                    }
                    className="flex-1 h-10 rounded border border-[color:var(--pos-color-border,#E2E8DF)] px-3 text-[13px] tabular-nums focus:border-[color:var(--pos-color-accent,var(--color-primary))] focus:outline-none"
                    data-testid="pos-membership-query" />
                  {memberLookupMode !== "phone" && (
                    <button type="button" onClick={lookupMember}
                      disabled={memberQuery.trim().length < 3 || memberStatus === "searching"}
                      data-testid="pos-membership-lookup"
                      className="h-10 px-3 rounded bg-[color:var(--color-primary)] text-white text-[12px] font-semibold hover:bg-[color:var(--color-primary-hover)] disabled:opacity-40 disabled:cursor-not-allowed">
                      {memberStatus === "searching" ? "…" : "Look up"}
                    </button>
                  )}
                  {memberLookupMode === "phone" && memberStatus === "searching" && (
                    <span className="text-[11px] text-[color:var(--pos-color-secondary-text,#5C6B64)] italic self-center px-2" data-testid="pos-membership-searching">searching…</span>
                  )}
                </div>
                {memberStatus === "not_found" && (
                  <div className="text-[11px] text-[color:var(--pos-color-secondary-text,#5C6B64)] mt-1 italic" data-testid="pos-membership-not-found">
                    No member found — treated as a new guest. Not an error.
                  </div>
                )}
              </div>
              {memberStatus === "found" && member && (
                <div className="rounded border border-emerald-200 bg-emerald-50 p-2" data-testid="pos-membership-card">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-[12px] font-semibold text-[color:var(--pos-color-primary-text,#1A2421)]" data-testid="pos-membership-name">{member.name}</div>
                      <div className="text-[10px] text-emerald-800 tabular-nums">{member.tier} · {member.phone} · {member.code}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-[9px] uppercase tracking-widest text-emerald-800">Points</div>
                      <div className="text-[16px] font-semibold tabular-nums text-[color:var(--pos-color-primary-text,#1A2421)]" data-testid="pos-membership-points">{member.points}</div>
                    </div>
                  </div>
                  <div className="mt-2 flex items-center gap-2">
                    <span className="text-[10px] uppercase tracking-widest text-[color:var(--pos-color-secondary-text,#5C6B64)]">Redeem</span>
                    <input type="number" value={redeemPts} min={0} max={member.points}
                      onChange={(e) => setRedeemPts(e.target.value)}
                      placeholder={`up to ${member.points}`}
                      className="h-9 w-32 rounded border border-emerald-200 px-2 text-[13px] tabular-nums focus:border-emerald-500 focus:outline-none"
                      data-testid="pos-membership-redeem" />
                    <span className="text-[10px] text-[color:var(--pos-color-secondary-text,#5C6B64)]">points · ₹{fmt2(Number(redeemPts || 0) * MEMBER_POINT_VALUE)}</span>
                  </div>
                </div>
              )}
            </div>
          )}

          {mode === "voucher" && (
            <div className="space-y-2" data-testid="pos-discount-voucher">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-[color:var(--pos-color-secondary-text,#5C6B64)] mb-1">
                  Voucher code
                </div>
                <div className="flex items-center gap-2">
                  <input type="text" value={voucherCode}
                    onChange={(e) => { setVoucherCode(e.target.value.toUpperCase()); setVoucher(null); setVoucherStatus("idle"); }}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); lookupVoucher(); }}}
                    autoFocus
                    placeholder="e.g. GIFT-500"
                    className="flex-1 h-10 rounded border border-[color:var(--pos-color-border,#E2E8DF)] px-3 text-[13px] font-mono tabular-nums focus:border-[color:var(--pos-color-accent,var(--color-primary))] focus:outline-none"
                    data-testid="pos-voucher-code" />
                  <button type="button" onClick={lookupVoucher}
                    disabled={voucherCode.trim().length < 3 || voucherStatus === "searching"}
                    data-testid="pos-voucher-authenticate"
                    className="h-10 px-3 rounded bg-[color:var(--color-primary)] text-white text-[12px] font-semibold hover:bg-[color:var(--color-primary-hover)] disabled:opacity-40 disabled:cursor-not-allowed">
                    {voucherStatus === "searching" ? "…" : "Authenticate"}
                  </button>
                </div>
                {voucherStatus === "not_found" && (
                  <div className="text-[11px] text-[color:var(--pos-color-secondary-text,#5C6B64)] mt-1 italic" data-testid="pos-voucher-not-found">
                    Voucher not found. Try a different code — not an error.
                  </div>
                )}
              </div>
              {voucherStatus === "found" && voucher && (
                <div className="rounded border border-emerald-200 bg-emerald-50 p-2" data-testid="pos-voucher-card">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-[12px] font-semibold text-[color:var(--pos-color-primary-text,#1A2421)]" data-testid="pos-voucher-code-display">{voucher.code}</div>
                      <div className="text-[10px] text-emerald-800">Valid until <span className="tabular-nums">{voucher.valid_until}</span></div>
                    </div>
                    <div className="text-right">
                      <div className="text-[9px] uppercase tracking-widest text-emerald-800">Value</div>
                      <div className="text-[16px] font-semibold tabular-nums text-[color:var(--pos-color-primary-text,#1A2421)]" data-testid="pos-voucher-value">₹{fmt2(voucher.value)}</div>
                    </div>
                  </div>
                  <div className="mt-1 text-[10px] text-emerald-900 italic">
                    Applied as a discount BEFORE tax — voucher value reduces the GST base.
                  </div>
                </div>
              )}
            </div>
          )}
          <div className="text-[11px] text-[color:var(--pos-color-secondary-text,#5C6B64)] bg-[color:var(--pos-color-raised-surface,#F9F9F8)] rounded p-2 space-y-0.5">
            <div className="flex justify-between"><span>Eligible net</span><span className="tabular-nums" data-testid="pos-discount-eligible-base">₹{fmt2(eligibleBase)}</span></div>
            {excludedCount > 0 && (
              <div className="flex justify-between text-amber-800" data-testid="pos-discount-excluded">
                <span>Excluded · {excludedCount} line{excludedCount === 1 ? "" : "s"} with line discount</span>
                <span className="tabular-nums">₹{fmt2(excludedBase)}</span>
              </div>
            )}
            <div className="flex justify-between"><span>Discount preview</span><span className="tabular-nums text-rose-700" data-testid="pos-discount-preview">− ₹{fmt2(previewDisc)}</span></div>
            <div className="flex justify-between font-semibold text-[color:var(--pos-color-primary-text,#1A2421)]"><span>New net</span><span className="tabular-nums">₹{fmt2(Math.max(0, totals.net_subtotal - previewDisc))}</span></div>
          </div>
          <div className="text-[10px] text-[color:var(--pos-color-secondary-text,#5C6B64)] italic">
            Authorised by <strong>system</strong>.
          </div>
        </div>
        <div className="p-3 border-t border-[color:var(--pos-color-border,#E2E8DF)] flex items-center justify-between shrink-0">
          <button type="button" onClick={() => onApply(null)}
            data-testid="pos-discount-clear"
            className="h-10 px-4 rounded border border-[color:var(--pos-color-border,#E2E8DF)] text-[12px] font-medium text-[color:var(--pos-color-secondary-text,#5C6B64)] hover:bg-[color:var(--pos-color-page-bg,#F4F6F2)]">
            Clear discount
          </button>
          <div className="flex items-center gap-2">
            <button type="button" onClick={onCancel}
              data-testid="pos-discount-cancel"
              className="h-10 px-3 rounded text-[12px] font-medium text-[color:var(--pos-color-secondary-text,#5C6B64)] hover:bg-[color:var(--pos-color-page-bg,#F4F6F2)]">Cancel</button>
            {mode === "membership"
              ? (
                <button type="button" onClick={applyMembership}
                  disabled={!member || !(Number(redeemPts) > 0)}
                  data-testid="pos-discount-apply-member"
                  className="h-10 px-4 rounded bg-[color:var(--color-primary)] text-white text-[12px] font-semibold hover:bg-[color:var(--color-primary-hover)] disabled:opacity-40">
                  Redeem points
                </button>
              )
              : mode === "voucher"
              ? (
                <button type="button" onClick={applyVoucher}
                  disabled={!voucher}
                  data-testid="pos-discount-apply-voucher"
                  className="h-10 px-4 rounded bg-[color:var(--color-primary)] text-white text-[12px] font-semibold hover:bg-[color:var(--color-primary-hover)] disabled:opacity-40">
                  Redeem voucher
                </button>
              )
              : (
                <button type="button" onClick={() => onApply({ type: mode, value: Number(value) })}
                  disabled={!(Number(value) > 0)}
                  data-testid="pos-discount-apply"
                  className="h-10 px-4 rounded bg-[color:var(--color-primary)] text-white text-[12px] font-semibold hover:bg-[color:var(--color-primary-hover)] disabled:opacity-40">
                  Apply · Enter
                </button>
              )
            }
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Split · Part 6 ─────────────────────────────────────────────────
// Two modes only (Part 3.1, Feb 2026): alcohol (auto-selects liquor
// lines), itemwise (manual per-line picker). Multi-tender already
// covers guests dividing a single bill — equal-split has been
// removed as a separate mode.
export function SplitDialog({ open, state, order, lines, onCancel, onConfirm, onRequestPinUnlock }) {
  const [mode, setMode]       = useState("alcohol");
  const [selection, setSelection] = useState(() => new Set()); // for itemwise: line_ids
  // POS Control Sprint · configure N children in one shot for the
  // common "Split 1 of 3 · Split 2 of 3 · Split 3 of 3" scenario. We
  // distribute lines round-robin into N groups so every child gets a
  // usable share; guests can then redistribute at the till with the
  // existing Move-lines flow. Range 2–4 covers 90 %+ of covers.
  const [evenN, setEvenN]     = useState(3);
  const printedLocked = order?.state === ORDER_STATE.PRINTED;
  useEffect(() => {
    if (!open) return;
    setMode("alcohol"); setSelection(new Set()); setEvenN(3);
    const h = (e) => { if (e.key === "Escape") { e.preventDefault(); onCancel(); } };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [open, onCancel]);
  if (!open) return null;

  const alcoholLines   = lines.filter((l) => l.item_id && String(l.item_id).startsWith("i_") && isLiquorLine(l, state));
  const foodLines      = lines.filter((l) => !isLiquorLine(l, state));

  // Round-robin distribute lines into N groups. Retains one line
  // per child on the parent because ORDER_SPLIT reducer expects the
  // parent to keep the remainder; we send ALL lines across N children
  // so the parent becomes an empty split parent (Contract §6).
  const evenGroups = (n) => {
    const groups = Array.from({ length: n }, (_, i) => ({
      line_ids: [],
      label:    `Split ${i + 1} of ${n}`,
    }));
    lines.forEach((l, i) => { groups[i % n].line_ids.push(l.id); });
    return groups.filter((g) => g.line_ids.length > 0);
  };

  const submit = () => {
    if (mode === "alcohol") {
      if (alcoholLines.length === 0) return;
      onConfirm({ groups: [
        { line_ids: alcoholLines.map((l) => l.id), label: "Alcohol" },
      ]});
      return;
    }
    if (mode === "itemwise") {
      const picked = [...selection];
      if (picked.length === 0) return;
      onConfirm({ groups: [
        { line_ids: picked, label: "Selected items" },
      ]});
      return;
    }
    if (mode === "even") {
      const groups = evenGroups(evenN);
      if (groups.length < 2) return;
      onConfirm({ groups });
      return;
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onCancel(); }}>
      <DialogContent className="max-w-[720px] w-[92vw] max-h-[560px] p-0 gap-0 border border-[color:var(--pos-color-border,#E2E8DF)] overflow-hidden flex flex-col"
        data-testid="pos-split-dialog">
        <div className="px-4 py-2 border-b border-[color:var(--pos-color-border,#E2E8DF)] flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-[color:var(--pos-color-secondary-text,#5C6B64)] flex items-center gap-1"><SplitIcon className="w-3 h-3" /> Split bill</div>
            <div className="text-[13px] font-semibold text-[color:var(--pos-color-primary-text,#1A2421)]">
              {lines.length} line{lines.length === 1 ? "" : "s"} · order state {order?.state}
            </div>
          </div>
          <button type="button" onClick={onCancel}
            className="h-7 px-2.5 rounded border border-[color:var(--pos-color-border,#E2E8DF)] text-[10px] text-[color:var(--pos-color-secondary-text,#5C6B64)]"
            data-testid="pos-split-close">Close · Esc</button>
        </div>

        {printedLocked && (
          <div className="bg-amber-50 border-b border-amber-200 p-3 flex items-center justify-between gap-3"
            data-testid="pos-split-printed-lock">
            <div className="text-[11px] text-amber-900 leading-snug">
              <div className="font-semibold flex items-center gap-1"><Lock className="w-3 h-3" /> Bill already printed</div>
              <div>Splitting after print voids the current invoice and reissues fresh numbers. Enter manager PIN to unlock.</div>
            </div>
            <button type="button" onClick={onRequestPinUnlock}
              data-testid="pos-split-request-pin"
              className="h-9 px-3 rounded bg-amber-700 text-white text-[11px] font-semibold hover:bg-amber-800">
              Enter manager PIN
            </button>
          </div>
        )}

        <div className="flex-1 grid grid-cols-[180px_1fr] divide-x divide-[color:var(--pos-color-border,#E2E8DF)] overflow-hidden">
          <div className="p-2 space-y-1" data-testid="pos-split-mode-nav">
            {[
              ["alcohol",  "Alcohol vs Food"],
              ["itemwise", "Item-wise"],
              ["even",     "Equal split"],
            ].map(([m, label]) => (
              <button key={m} type="button" onClick={() => setMode(m)}
                disabled={printedLocked}
                data-testid={`pos-split-mode-${m}`}
                className={`w-full text-left h-10 px-3 rounded border text-[12px] font-medium transition
                  ${mode === m
                    ? "border-[color:var(--color-primary)] bg-[color:var(--color-primary)]/[0.06] text-[color:var(--pos-color-primary-text,#1A2421)]"
                    : "border-[color:var(--pos-color-border,#E2E8DF)] bg-white text-[color:var(--pos-color-primary-text,#1A2421)] hover:bg-[color:var(--pos-color-raised-surface,#F9F9F8)]"}
                  disabled:opacity-40 disabled:cursor-not-allowed`}>
                {label}
              </button>
            ))}
          </div>
          <div className="p-3 overflow-y-auto">
            {mode === "alcohol" && (
              <div className="space-y-2" data-testid="pos-split-alcohol">
                <div className="text-[11px] text-[color:var(--pos-color-secondary-text,#5C6B64)]">
                  Moves every liquor line into a NEW child order. The parent keeps food.
                </div>
                <div className="rounded border border-[color:var(--pos-color-border,#E2E8DF)] divide-y divide-[color:var(--pos-color-border,#E2E8DF)]">
                  <SplitGroup label="Food (stays on parent)" lines={foodLines} />
                  <SplitGroup label="Alcohol (new child)" lines={alcoholLines} highlight />
                </div>
                {alcoholLines.length === 0 && <div className="text-[11px] italic text-rose-700">No liquor lines to split.</div>}
              </div>
            )}
            {mode === "itemwise" && (
              <div className="space-y-2" data-testid="pos-split-itemwise">
                <div className="text-[11px] text-[color:var(--pos-color-secondary-text,#5C6B64)]">
                  Pick lines to move into ONE new child. Repeat the split flow for further splits.
                </div>
                <div className="rounded border border-[color:var(--pos-color-border,#E2E8DF)] divide-y divide-[color:var(--pos-color-border,#E2E8DF)]">
                  {lines.map((l) => {
                    const on = selection.has(l.id);
                    return (
                      <button key={l.id} type="button"
                        onClick={() => {
                          const next = new Set(selection);
                          if (on) next.delete(l.id); else next.add(l.id);
                          setSelection(next);
                        }}
                        data-testid={`pos-split-pick-${l.id}`}
                        className={`w-full text-left px-3 py-2 flex items-center justify-between text-[12px]
                          ${on ? "bg-[color:var(--color-primary)]/[0.08]" : "bg-white hover:bg-[color:var(--pos-color-raised-surface,#F9F9F8)]"}`}>
                        <span className="inline-flex items-center gap-2">
                          <input type="checkbox" readOnly checked={on} />
                          {l.item_name} × {l.qty}
                        </span>
                        <span className="tabular-nums">₹{fmt2(l.qty * l.unit_price)}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            {mode === "even" && (
              <div className="space-y-3" data-testid="pos-split-even">
                <div className="text-[11px] text-[color:var(--pos-color-secondary-text,#5C6B64)]">
                  Split the order into equal child bills — lines are distributed round-robin. Every child gets its own invoice number and settles independently.
                </div>
                <div className="flex items-center gap-2" data-testid="pos-split-even-count-nav">
                  <span className="text-[11px] text-[color:var(--pos-color-secondary-text,#5C6B64)]">Children:</span>
                  {[2, 3, 4].map((n) => (
                    <button key={n} type="button" onClick={() => setEvenN(n)}
                      data-testid={`pos-split-even-${n}`}
                      className={`h-8 w-10 rounded border text-[12px] font-semibold transition
                        ${evenN === n
                          ? "border-[color:var(--color-primary)] bg-[color:var(--color-primary)]/[0.08] text-[color:var(--pos-color-primary-text,#1A2421)]"
                          : "border-[color:var(--pos-color-border,#E2E8DF)] bg-white text-[color:var(--pos-color-primary-text,#1A2421)] hover:bg-[color:var(--pos-color-raised-surface,#F9F9F8)]"}`}>
                      {n}
                    </button>
                  ))}
                </div>
                <div className={`grid gap-2 ${evenN === 2 ? "grid-cols-2" : evenN === 3 ? "grid-cols-3" : "grid-cols-2"}`}>
                  {(() => {
                    const groups = evenGroups(evenN);
                    return groups.map((g, gi) => {
                      const glines = g.line_ids.map((id) => lines.find((l) => l.id === id)).filter(Boolean);
                      const gtot = glines.reduce((s, l) => s + l.qty * l.unit_price, 0);
                      return (
                        <div key={gi} className="rounded border border-purple-200 bg-purple-50/40 p-2"
                          data-testid={`pos-split-even-preview-${gi + 1}`}>
                          <div className="text-[10px] uppercase tracking-wider text-purple-800 font-semibold mb-1">
                            Split {gi + 1} of {evenN} · {glines.length} line{glines.length === 1 ? "" : "s"}
                          </div>
                          {glines.map((l) => (
                            <div key={l.id} className="text-[11px] flex items-center justify-between py-0.5">
                              <span className="truncate">{l.item_name} × {l.qty}</span>
                              <span className="tabular-nums">₹{fmt2(l.qty * l.unit_price)}</span>
                            </div>
                          ))}
                          <div className="mt-1 pt-1 border-t border-purple-200 flex items-center justify-between text-[11px] font-semibold">
                            <span>Sub-total</span>
                            <span className="tabular-nums">₹{fmt2(gtot)}</span>
                          </div>
                        </div>
                      );
                    });
                  })()}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="border-t border-[color:var(--pos-color-border,#E2E8DF)] px-3 py-2 flex items-center justify-end gap-2">
          <button type="button" onClick={onCancel}
            data-testid="pos-split-cancel"
            className="h-10 px-3 rounded text-[12px] font-medium text-[color:var(--pos-color-secondary-text,#5C6B64)] hover:bg-[color:var(--pos-color-page-bg,#F4F6F2)]">
            Cancel
          </button>
          <button type="button" onClick={submit}
            disabled={printedLocked}
            data-testid="pos-split-confirm"
            className="h-10 px-4 rounded bg-[color:var(--color-primary)] text-white text-[12px] font-semibold hover:bg-[color:var(--color-primary-hover)] disabled:opacity-40 disabled:cursor-not-allowed">
            Confirm split
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function SplitGroup({ label, lines, highlight }) {
  return (
    <div className={`p-2 ${highlight ? "bg-purple-50/70" : "bg-white"}`}>
      <div className={`text-[10px] uppercase tracking-wider font-semibold mb-1 ${highlight ? "text-purple-800" : "text-[color:var(--pos-color-secondary-text,#5C6B64)]"}`}>
        {label} · {lines.length}
      </div>
      {lines.length === 0 && <div className="text-[11px] italic text-[color:var(--pos-color-secondary-text,#5C6B64)]">(empty)</div>}
      {lines.map((l) => (
        <div key={l.id} className="text-[11px] flex items-center justify-between py-0.5">
          <span className="truncate">{l.item_name} × {l.qty}</span>
          <span className="tabular-nums">₹{fmt2(l.qty * l.unit_price)}</span>
        </div>
      ))}
    </div>
  );
}
function isLiquorLine(line, state) {
  // Cheap check: exploit mock item ids we know are liquor. Prevents a
  // circular import back into mockData.ITEMS while retaining accuracy.
  const LIQUOR_PREFIXES = ["i_kingfisher", "i_bud_pint", "i_corona", "i_heineken", "i_pina_colada",
    "i_mojito", "i_long_island", "i_gin_tonic", "i_whiskey_peg", "i_vodka_peg", "i_rum_peg",
    "i_red_wine", "i_white_wine", "i_beer_pitcher", "i_sangria"];
  return LIQUOR_PREFIXES.includes(line.item_id);
}

// ─── Manager PIN dialog · Part 6 ────────────────────────────────────
export function ManagerPinDialog({ open, expectedPin = "1234", onCancel, onOk }) {
  const [pin, setPin] = useState("");
  const [error, setError] = useState(false);
  const inputRef = useRef(null);
  useEffect(() => {
    if (!open) return;
    setPin(""); setError(false);
    setTimeout(() => inputRef.current?.focus(), 30);
  }, [open]);
  if (!open) return null;
  const submit = () => {
    if (pin !== expectedPin) { setError(true); return; }
    onOk(pin);
  };
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onCancel(); }}>
      <DialogContent className="max-w-[400px] p-0 gap-0 border border-amber-200 overflow-hidden"
        data-testid="pos-pin-dialog">
        <div className="px-4 py-3 border-b border-amber-100 bg-amber-50 rounded-t-lg">
          <div className="text-[10px] uppercase tracking-wider text-amber-900">Manager PIN required</div>
          <div className="text-[13px] font-semibold text-[color:var(--pos-color-primary-text,#1A2421)]">Post-print split · reissue invoices</div>
          <div className="text-[10px] text-amber-900 mt-0.5 italic">Enter your manager PIN to authorise.</div>
        </div>
        <div className="p-4 space-y-2">
          <input ref={inputRef} type="password" inputMode="numeric" value={pin}
            onChange={(e) => { setPin(e.target.value.replace(/[^0-9]/g, "").slice(0, 6)); setError(false); }}
            onKeyDown={(e) => { if (e.key === "Enter") submit(); if (e.key === "Escape") onCancel(); }}
            placeholder="4-digit PIN"
            className={`w-full h-12 rounded border text-[24px] font-mono tabular-nums text-center tracking-widest px-3 focus:outline-none
              ${error ? "border-rose-500 bg-rose-50" : "border-[color:var(--pos-color-border,#E2E8DF)] bg-white focus:border-[color:var(--pos-color-accent,var(--color-primary))]"}`}
            data-testid="pos-pin-input" />
          {error && <div className="text-[11px] text-rose-700" data-testid="pos-pin-error">Incorrect PIN.</div>}
        </div>
        <div className="p-3 border-t border-[color:var(--pos-color-border,#E2E8DF)] flex items-center justify-end gap-2">
          <button type="button" onClick={onCancel}
            data-testid="pos-pin-cancel"
            className="h-10 px-3 rounded text-[12px] font-medium text-[color:var(--pos-color-secondary-text,#5C6B64)] hover:bg-[color:var(--pos-color-page-bg,#F4F6F2)]">Cancel</button>
          <button type="button" onClick={submit}
            data-testid="pos-pin-ok"
            className="h-10 px-4 rounded bg-amber-700 text-white text-[12px] font-semibold hover:bg-amber-800">
            Authorise · Enter
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Transfer · Part 7 ──────────────────────────────────────────────
// Two modes: whole table transfer, or item-wise transfer. Destination
// tables that already hold an active order are shown but disabled
// (refuse rather than merge).
export function TransferDialog({ open, state, order, lines, onCancel, onTransferTable, onTransferItems }) {
  const [mode, setMode] = useState("table");
  const [dest, setDest] = useState(null);
  const [picked, setPicked] = useState(() => new Set());
  useEffect(() => {
    if (!open) return;
    setMode("table"); setDest(null); setPicked(new Set());
    const h = (e) => { if (e.key === "Escape") { e.preventDefault(); onCancel(); } };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [open, onCancel]);
  if (!open) return null;
  const dineIn = order?.type === ORDER_TYPE.DINE_IN;
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onCancel(); }}>
      <DialogContent className="max-w-[720px] w-[92vw] max-h-[600px] p-0 gap-0 border border-[color:var(--pos-color-border,#E2E8DF)] overflow-hidden flex flex-col"
        data-testid="pos-transfer-dialog">
        <div className="px-4 py-2 border-b border-[color:var(--pos-color-border,#E2E8DF)]">
          <div className="text-[10px] uppercase tracking-wider text-[color:var(--pos-color-secondary-text,#5C6B64)] flex items-center gap-1"><ArrowLeftRight className="w-3 h-3" /> Transfer</div>
          <div className="text-[13px] font-semibold text-[color:var(--pos-color-primary-text,#1A2421)]">
            From {TABLES.find((t) => t.id === order?.table_id)?.label || (order?.token || "current order")}
          </div>
        </div>
        <div className="flex-1 grid grid-cols-[180px_1fr] divide-x divide-[color:var(--pos-color-border,#E2E8DF)] overflow-hidden">
          <div className="p-2 space-y-1" data-testid="pos-transfer-mode-nav">
            <button type="button" onClick={() => setMode("table")}
              disabled={!dineIn}
              data-testid="pos-transfer-mode-table"
              className={`w-full text-left h-10 px-3 rounded border text-[12px] font-medium
                ${mode === "table" ? "border-[color:var(--color-primary)] bg-[color:var(--color-primary)]/[0.06] text-[color:var(--pos-color-primary-text,#1A2421)]" : "border-[color:var(--pos-color-border,#E2E8DF)] bg-white text-[color:var(--pos-color-primary-text,#1A2421)] hover:bg-[color:var(--pos-color-raised-surface,#F9F9F8)]"}
                disabled:opacity-40 disabled:cursor-not-allowed`}>
              Whole table
            </button>
            <button type="button" onClick={() => setMode("items")}
              data-testid="pos-transfer-mode-items"
              className={`w-full text-left h-10 px-3 rounded border text-[12px] font-medium
                ${mode === "items" ? "border-[color:var(--color-primary)] bg-[color:var(--color-primary)]/[0.06] text-[color:var(--pos-color-primary-text,#1A2421)]" : "border-[color:var(--pos-color-border,#E2E8DF)] bg-white text-[color:var(--pos-color-primary-text,#1A2421)] hover:bg-[color:var(--pos-color-raised-surface,#F9F9F8)]"}`}>
              Items
            </button>
          </div>
          <div className="p-3 overflow-y-auto">
            {mode === "table" && dineIn && (
              <div className="space-y-2" data-testid="pos-transfer-table-picker">
                <div className="text-[11px] text-[color:var(--pos-color-secondary-text,#5C6B64)]">
                  Pick a destination table. Tables holding an active order are shown but disabled — collisions are refused rather than merged.
                </div>
                <div className="grid grid-cols-6 gap-2">
                  {TABLES.map((t) => {
                    if (t.id === order?.table_id) return null;
                    const st = tableState(state, t.id);
                    const blocked = st === "OCCUPIED" || st === "BILLED" || st === "UNAVAILABLE";
                    const on = dest === t.id;
                    return (
                      <button key={t.id} type="button"
                        disabled={blocked}
                        onClick={() => setDest(t.id)}
                        data-testid={`pos-transfer-dest-${t.id}`}
                        className={`aspect-square rounded border text-[12px] font-medium transition
                          ${on ? "border-[color:var(--color-primary)] bg-[color:var(--color-primary)] text-white"
                          : blocked ? "border-slate-200 bg-slate-50 text-slate-400 cursor-not-allowed"
                          : "border-[color:var(--pos-color-border,#E2E8DF)] bg-white text-[color:var(--pos-color-primary-text,#1A2421)] hover:bg-[color:var(--pos-color-raised-surface,#F9F9F8)]"}`}>
                        {t.label}
                        <div className="text-[9px] opacity-70">{st}</div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            {mode === "table" && !dineIn && (
              <div className="text-[12px] italic text-[color:var(--pos-color-secondary-text,#5C6B64)]">
                Whole-table transfer only applies to dine-in orders.
              </div>
            )}
            {mode === "items" && (
              <div className="space-y-2" data-testid="pos-transfer-items-picker">
                <div className="text-[11px] text-[color:var(--pos-color-secondary-text,#5C6B64)]">
                  Pick lines to move. Items are cloned onto the destination table&apos;s active order; fired lines carry their KOT tag.
                </div>
                <div className="rounded border border-[color:var(--pos-color-border,#E2E8DF)] divide-y divide-[color:var(--pos-color-border,#E2E8DF)]">
                  {lines.map((l) => {
                    const on = picked.has(l.id);
                    return (
                      <button key={l.id} type="button"
                        onClick={() => {
                          const next = new Set(picked);
                          if (on) next.delete(l.id); else next.add(l.id);
                          setPicked(next);
                        }}
                        data-testid={`pos-transfer-line-${l.id}`}
                        className={`w-full text-left px-3 py-2 flex items-center justify-between text-[12px]
                          ${on ? "bg-[color:var(--color-primary)]/[0.08]" : "bg-white hover:bg-[color:var(--pos-color-raised-surface,#F9F9F8)]"}`}>
                        <span className="inline-flex items-center gap-2">
                          <input type="checkbox" readOnly checked={on} />
                          {l.item_name} × {l.qty} {l.ticket_id ? <span className="text-[9px] uppercase text-sky-800 bg-sky-100 rounded px-1">{l.ticket_id}</span> : null}
                        </span>
                        <span className="tabular-nums">₹{fmt2(l.qty * l.unit_price)}</span>
                      </button>
                    );
                  })}
                </div>
                <div className="text-[11px] text-[color:var(--pos-color-secondary-text,#5C6B64)]">Destination:</div>
                <div className="grid grid-cols-6 gap-2">
                  {TABLES.map((t) => {
                    if (t.id === order?.table_id) return null;
                    const st = tableState(state, t.id);
                    const activeDest = activeOrderForTable(state, t.id);
                    // For item transfer we NEED an active order on the destination.
                    const usable = !!activeDest;
                    const on = dest === t.id;
                    return (
                      <button key={t.id} type="button"
                        disabled={!usable || st === "UNAVAILABLE"}
                        onClick={() => setDest(t.id)}
                        data-testid={`pos-transfer-items-dest-${t.id}`}
                        className={`aspect-square rounded border text-[12px] font-medium transition
                          ${on ? "border-[color:var(--color-primary)] bg-[color:var(--color-primary)] text-white"
                          : !usable ? "border-slate-200 bg-slate-50 text-slate-400 cursor-not-allowed"
                          : "border-[color:var(--pos-color-border,#E2E8DF)] bg-white text-[color:var(--pos-color-primary-text,#1A2421)] hover:bg-[color:var(--pos-color-raised-surface,#F9F9F8)]"}`}>
                        {t.label}
                        <div className="text-[9px] opacity-70">{st}</div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="border-t border-[color:var(--pos-color-border,#E2E8DF)] px-3 py-2 flex items-center justify-end gap-2">
          <button type="button" onClick={onCancel}
            data-testid="pos-transfer-cancel"
            className="h-10 px-3 rounded text-[12px] font-medium text-[color:var(--pos-color-secondary-text,#5C6B64)] hover:bg-[color:var(--pos-color-page-bg,#F4F6F2)]">Cancel</button>
          <button type="button"
            disabled={!dest || (mode === "items" && picked.size === 0)}
            onClick={() => {
              if (mode === "table") onTransferTable(dest);
              else onTransferItems(dest, [...picked]);
            }}
            data-testid="pos-transfer-confirm"
            className="h-10 px-4 rounded bg-[color:var(--color-primary)] text-white text-[12px] font-semibold hover:bg-[color:var(--color-primary-hover)] disabled:opacity-40 disabled:cursor-not-allowed">
            Confirm transfer
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Open Item Dialog · Part 8 ──────────────────────────────────────
export function OpenItemDialog({ open, onCancel, onPunch, mode = "punch" }) {
  const isCreateMode = mode === "create";
  const [category, setCategory] = useState("food");
  const [name, setName] = useState("");
  const [price, setPrice] = useState("");
  const [qty, setQty] = useState(1);
  useEffect(() => {
    if (!open) return;
    setCategory("food"); setName(""); setPrice(""); setQty(1);
    const h = (e) => {
      if (e.key === "Escape") { e.preventDefault(); onCancel(); }
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [open, onCancel]);
  if (!open) return null;
  const valid = name.trim().length > 0 && Number(price) > 0 && (isCreateMode || Number(qty) > 0);
  const submit = () => {
    if (!valid) return;
    onPunch({ category, name: name.trim(), price: Number(price), qty: isCreateMode ? 1 : Number(qty) });
  };
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onCancel(); }}>
      <DialogContent className="max-w-[520px] p-0 gap-0 border border-emerald-200 overflow-hidden"
        data-testid="pos-open-item-dialog">
        <div className="px-4 py-3 border-b border-emerald-100 bg-emerald-50 rounded-t-lg">
          <div className="text-[10px] uppercase tracking-wider text-emerald-800">
            {isCreateMode ? "Open items · create catalog entry" : "Open item · off-menu punch"}
          </div>
          <div className="text-[13px] font-semibold text-[color:var(--pos-color-primary-text,#1A2421)]">
            {isCreateMode
              ? "Manager-authorised · persists for the business day, clears at close"
              : "Permission-gated · every punch is stamped with the user id"}
          </div>
        </div>
        <div className="p-4 space-y-3">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-[color:var(--pos-color-secondary-text,#5C6B64)] mb-1.5">Category · derives tax + routing</div>
            <div className="grid grid-cols-4 gap-1.5" data-testid="pos-open-item-category">
              {[["food", "Food"], ["beverage", "Beverage"], ["alcohol", "Alcohol"], ["other", "Other"]].map(([id, label]) => (
                <button key={id} type="button" onClick={() => setCategory(id)}
                  data-testid={`pos-open-item-cat-${id}`}
                  className={`h-11 rounded border text-[12px] font-medium
                    ${category === id
                      ? "border-emerald-600 bg-emerald-600 text-white"
                      : "border-[color:var(--pos-color-border,#E2E8DF)] bg-white text-[color:var(--pos-color-primary-text,#1A2421)] hover:bg-[color:var(--pos-color-raised-surface,#F9F9F8)]"}`}>
                  {label}
                </button>
              ))}
            </div>
            <div className="text-[10px] text-[color:var(--pos-color-secondary-text,#5C6B64)] mt-1 italic">
              {category === "alcohol" ? "20% VAT · Bar" :
               category === "beverage" ? "5% GST · Bar" :
               category === "food" ? "5% GST · Kitchen" :
               "5% GST · Kitchen (default)"}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-[color:var(--pos-color-secondary-text,#5C6B64)] mb-1">Item name</div>
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} autoFocus
              placeholder="e.g. Freshly-shucked oyster · seasonal"
              className="w-full h-11 rounded border border-[color:var(--pos-color-border,#E2E8DF)] px-3 text-[13px] focus:border-emerald-500 focus:outline-none"
              data-testid="pos-open-item-name" />
          </div>
          <div className={isCreateMode ? "" : "grid grid-cols-2 gap-2"}>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-[color:var(--pos-color-secondary-text,#5C6B64)] mb-1">Price per serve</div>
              <input type="number" value={price} min={0} onChange={(e) => setPrice(e.target.value)}
                placeholder="₹"
                className="w-full h-11 rounded border border-[color:var(--pos-color-border,#E2E8DF)] px-3 text-[13px] tabular-nums focus:border-emerald-500 focus:outline-none"
                data-testid="pos-open-item-price" />
            </div>
            {!isCreateMode && (
              <div>
                <div className="text-[10px] uppercase tracking-wider text-[color:var(--pos-color-secondary-text,#5C6B64)] mb-1">Quantity</div>
                <input type="number" value={qty} min={1} onChange={(e) => setQty(e.target.value)}
                  className="w-full h-11 rounded border border-[color:var(--pos-color-border,#E2E8DF)] px-3 text-[13px] tabular-nums focus:border-emerald-500 focus:outline-none"
                  data-testid="pos-open-item-qty" />
              </div>
            )}
          </div>
        </div>
        <div className="p-3 border-t border-[color:var(--pos-color-border,#E2E8DF)] flex items-center justify-end gap-2">
          <button type="button" onClick={onCancel}
            data-testid="pos-open-item-cancel"
            className="h-10 px-3 rounded text-[12px] font-medium text-[color:var(--pos-color-secondary-text,#5C6B64)] hover:bg-[color:var(--pos-color-page-bg,#F4F6F2)]">Cancel</button>
          <button type="button" onClick={submit} disabled={!valid}
            data-testid="pos-open-item-punch"
            className="h-10 px-4 rounded bg-emerald-700 text-white text-[12px] font-semibold hover:bg-emerald-800 disabled:opacity-40 disabled:cursor-not-allowed">
            {isCreateMode ? "Save open item" : "Punch open item"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
